/**
 * WebSocket Store (Pinia)
 *
 * @version 1.0.0
 * @description Handles WebSocket connections via Laravel Echo + Pusher.
 * @migrated-from store/websocket/websocket.ts (Vuex 4)
 *
 * Cross-store dependencies (called lazily inside actions — no circular import risk):
 *   - useAuthStore      → user.email, user.agent_id
 *   - useProspectsStore  → fetchProspects(), fetchHighlightedCount()
 *   - useCustomersStore  → fetchCustomers(), fetchHighlightedCount()
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import Echo from 'laravel-echo'
import Pusher from 'pusher-js'
import axios from 'axios'
import { Md5 } from 'ts-md5'
import router from '@/router'
import { useAuthStore } from '@/stores/auth'
import { useProspectsStore } from '@/stores/useProspectsStore'
import { useCustomersStore } from '@/stores/useCustomersStore'
import { useNotificationsStore } from '@/stores/useNotificationsStore'

// Make Pusher available globally for Laravel Echo
window.Pusher = Pusher

// =============================================================================
// Types
// =============================================================================

export interface PresenceUser {
  email: string
  [key: string]: any
}

// =============================================================================
// Store
// =============================================================================

export const useWebsocketStore = defineStore('websocket', () => {
  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  const channelName = ref('')
  const isConnected = ref(false)
  const usersOnline = ref<string[]>([])
  const commonChannel = ref<string | null>(null)
  const privateChannel = ref<string | null>(null)
  /** F4 — canale privato per utente `App.Models.User.{id}` (Notification broadcast) */
  const userChannel = ref<string | null>(null)
  /** Totem — il canale comune è effettivamente sottoscritto (subscription_succeeded). */
  const commonChannelSubscribed = ref(false)
  /** Totem — ultimo `NewReportsAvailable` ricevuto e quanti dall'avvio (diagnostica). */
  const lastReportsEventAt = ref<Date | null>(null)
  const reportsEventsCount = ref(0)

  // ---------------------------------------------------------------------------
  // Getters
  // ---------------------------------------------------------------------------

  const usersOnlineCount = computed(() => isConnected.value ? usersOnline.value.length : 0)

  const channel = computed(() => {
    if (window.Echo) {
      return window.Echo.channel(channelName.value)
    }
    return undefined
  })

  // ---------------------------------------------------------------------------
  // Actions — Connection lifecycle
  // ---------------------------------------------------------------------------

  /**
   * Initialize and connect to WebSocket server.
   * Creates a new Echo instance if one does not exist, then joins the
   * admin-presence channel for online-user tracking.
   */
  function connect(): void {
    const authStore = useAuthStore()

    if (!window.Echo) {
      const pusherKey = import.meta.env.VITE_PUSHER_APP_KEY
      const pusherCluster = import.meta.env.VITE_PUSHER_APP_CLUSTER
      const pusherPort = import.meta.env.VITE_PUSHER_PORT
      const pusherUseSsl = import.meta.env.VITE_PUSHER_USE_SSL === 'true'
      const apiUrl = import.meta.env.VITE_API_URL

      console.log('Initializing Laravel Echo with Pusher')

      window.Echo = new Echo({
        broadcaster: 'pusher',
        key: pusherKey,
        cluster: pusherCluster,
        wsHost: window.location.hostname,
        wsPort: Number(pusherPort),
        wssPort: Number(pusherPort),
        forceTLS: pusherUseSsl,
        enableStats: false,
        disabledTransports: ['xhr_streaming', 'xhr_polling', 'sockjs'],
        enabledTransports: ['ws', 'wss'],
        authEndpoint: `${apiUrl}/broadcasting/auth`,
        authorizer: (ch: any, _options: any) => ({
          authorize: (socketId: string, callback: Function) => {
            console.log('Authorizing WebSocket channel:', ch.name)
            axios
              .post('/broadcasting/auth', {
                socket_id: socketId,
                channel_name: ch.name,
              })
              .then((response: any) => {
                console.log(`WebSocket auth response for ${ch.name}:`, response?.status, response?.data)
                if (response?.data?.auth) {
                  callback(false, response.data)
                } else {
                  console.error(`WebSocket auth for ${ch.name}: missing auth key in response`, response?.data)
                  callback(true, { error: 'Auth response missing auth key' })
                }
              })
              .catch((error: any) => {
                const status = error?.status ?? error?.response?.status ?? '?'
                const message = error?.data?.message ?? error?.response?.data?.message ?? error?.message ?? 'unknown'
                console.error(`WebSocket auth failed for ${ch.name}: [${status}] ${message}`, error)
                callback(true, error)
              })
          },
        }),
      })

      // Bind connection state changes
      window.Echo.connector.pusher.connection.bind('state_change', (states: any) => {
        console.log('WebSocket state changed:', states.current)
        _setConnected(states.current === 'connected')
      })

      // Bind error handler
      window.Echo.connector.pusher.connection.bind('error', (err: any) => {
        console.error('WebSocket error:', err)
        if (err.error?.data?.code === 4004) {
          console.warn('WebSocket connection limit reached')
        }
      })
    }

    // Connect
    window.Echo.connector.pusher.connect()

    // Join presence channel
    window.Echo.join('admin-presence')
      .here((users: PresenceUser[]) => {
        _setUsersOnline(users)
      })
      .joining((user: PresenceUser) => {
        _userJoined(user)
      })
      .leaving((user: PresenceUser) => {
        if (authStore.user != null) {
          const currentUserHash = Md5.hashStr(authStore.user.email)
          if (currentUserHash !== user.email) {
            _userLeft(user)
          }
        }
      })
  }

  /**
   * Disconnect from WebSocket server.
   */
  function disconnect(): void {
    if (window.Echo) {
      window.Echo.connector.pusher.disconnect()
    }
    usersOnline.value = []
    userChannel.value = null // F4: al riconnetti si ri-sottoscrive il canale utente
    commonChannelSubscribed.value = false
    _setConnected(false)
    useNotificationsStore().setRealtime(false)
  }

  /**
   * Reconnect to WebSocket server.
   */
  function reconnect(): void {
    disconnect()
    connect()
  }

  // ---------------------------------------------------------------------------
  // Actions — Channel listeners
  // ---------------------------------------------------------------------------

  /**
   * Listen to common channel for broadcast events.
   * Currently handles `NewReportsAvailable` events.
   */
  function listenToCommonChannel(): void {
    if (!window.Echo) {
      console.warn('Echo not initialized, cannot listen to common channel')
      return
    }

    const currCommonChannel = 'gandolfi-group'

    // `Echo.private()` è (ri)chiamata SEMPRE: su un canale già noto pusher-js
    // rifà `channel.subscribe()`, cioè ritenta l'autorizzazione se la prima
    // era fallita (es. token non ancora pronto al boot) — è il retry che le
    // vecchie chiamate ridondanti facevano senza volerlo.
    const channel = window.Echo.private(currCommonChannel)

    // Il LISTENER invece va attaccato una volta sola: viene chiamata da
    // onLogin, onUserLoaded e dal boot, e Echo accoda un listener a ogni
    // chiamata → lo stesso evento arrivava N volte (N refresh sul totem).
    if (commonChannel.value === currCommonChannel) return

    console.log('Listening to common channel:', currCommonChannel)

    // Stato della sottoscrizione letto da pusher-js, non da Echo: il
    // `subscribed()` di Echo non si è dimostrato affidabile (restava a false
    // con il canale attivo). `pusherChannel.subscribed` è la verità del
    // client; gli eventi pusher:* aggiornano il ref, e chi vuole può
    // rileggerlo live con isCommonChannelSubscribed().
    const pusherChannel = window.Echo.connector?.pusher?.channel(`private-${currCommonChannel}`)
    if (pusherChannel) {
      commonChannelSubscribed.value = !!pusherChannel.subscribed
      pusherChannel.bind('pusher:subscription_succeeded', () => {
        commonChannelSubscribed.value = true
        console.log('[WS] common channel subscribed:', currCommonChannel)
      })
      pusherChannel.bind('pusher:subscription_error', (err: unknown) => {
        commonChannelSubscribed.value = false
        console.error('[WS] common channel subscription error:', err)
      })
    }

    channel
      .listen('NewReportsAvailable', (e: any) => {
        console.log('Received NewReportsAvailable event:', e)
        lastReportsEventAt.value = new Date()
        reportsEventsCount.value += 1
        window.emitter?.emit('refreshInterventions')
      })

    commonChannel.value = currCommonChannel
  }

  /** Stato LIVE della sottoscrizione al canale comune (fonte: pusher-js). */
  function isCommonChannelSubscribed(): boolean {
    if (!commonChannel.value || !window.Echo) return false
    const ch = window.Echo.connector?.pusher?.channel(`private-${commonChannel.value}`)
    const live = !!ch?.subscribed
    if (live !== commonChannelSubscribed.value) commonChannelSubscribed.value = live
    return live
  }

  /**
   * Listen to private agent channel for user-specific events.
   * Handles `ProspectsUpdated` and `CustomersUpdated` events by refreshing
   * the corresponding Pinia stores when user is on the relevant page.
   */
  function listenToPrivateChannel(): void {
    if (!window.Echo) {
      console.warn('Echo not initialized, cannot listen to private channel')
      return
    }

    const authStore = useAuthStore()
    console.log('[WS] listenToPrivateChannel called, user:', authStore.user?.email, 'agent_id:', authStore.user?.agent_id)

    if (authStore.user == null || authStore.user.agent_id == null) {
      console.log('[WS] Agent ID is null, cannot subscribe to private channel')
      return
    }

    // Leave previous channel if exists
    if (privateChannel.value) {
      window.Echo.leave(privateChannel.value)
    }

    const currPrivateChannel = `agent.${authStore.user.agent_id}`
    console.log('Listening to private channel:', currPrivateChannel)

    window.Echo.private(currPrivateChannel)
      .listen('ProspectsUpdated', (e: any) => {
        console.log('Received ProspectsUpdated event:', e)
        const currentPath = router.currentRoute.value.fullPath
        const prospectsStore = useProspectsStore()
        if (currentPath.endsWith('/contacts/prospects')) {
          prospectsStore.fetchProspects().catch(() => {})
        }
        prospectsStore.fetchHighlightedCount().catch(() => {})
      })
      .listen('CustomersUpdated', (e: any) => {
        console.log('Received CustomersUpdated event:', e)
        const currentPath = router.currentRoute.value.fullPath
        const customersStore = useCustomersStore()
        if (currentPath.endsWith('/contacts/customers')) {
          customersStore.fetchCustomers().catch(() => {})
        }
        customersStore.fetchHighlightedCount().catch(() => {})
      })

    privateChannel.value = currPrivateChannel
  }

  /**
   * F4 — Sottoscrive il canale privato dell'utente per le Notification
   * `broadcast` del motore eventi (EventNotification): ogni notifica in-app
   * arriva in tempo reale alla campanella (useNotificationsStore.handleIncoming).
   * Idempotente: se il canale è già quello dell'utente corrente non fa nulla.
   */
  function listenToUserChannel(): void {
    const authStore = useAuthStore()
    if (!window.Echo || authStore.user?.id == null) return

    const curr = `App.Models.User.${authStore.user.id}`
    if (userChannel.value === curr) return
    if (userChannel.value) {
      window.Echo.leave(userChannel.value)
    }

    const notificationsStore = useNotificationsStore()
    window.Echo.private(curr).notification((payload: any) => {
      notificationsStore.handleIncoming(payload)
    })
    userChannel.value = curr
    notificationsStore.setRealtime(true)
    console.log('[WS] listening to user channel:', curr)
  }

  function leaveUserChannel(): void {
    if (window.Echo && userChannel.value) {
      window.Echo.leave(userChannel.value)
    }
    userChannel.value = null
    useNotificationsStore().setRealtime(false)
  }

  // ---------------------------------------------------------------------------
  // Internal helpers (replace Vuex mutations)
  // ---------------------------------------------------------------------------

  function _setConnected(value: boolean): void {
    isConnected.value = value
    // F4: con il socket giù la campanella torna al poll da 60s
    if (userChannel.value) {
      useNotificationsStore().setRealtime(value)
    }
  }

  function _setUsersOnline(users: PresenceUser[]): void {
    usersOnline.value =
      Array.isArray(users) ? users.map((u) => u.email) : []
  }

  function _userJoined(user: PresenceUser): void {
    if (!usersOnline.value.includes(user.email)) {
      usersOnline.value.push(user.email)
    }
  }

  function _userLeft(user: PresenceUser): void {
    const index = usersOnline.value.indexOf(user.email)
    if (index >= 0) {
      usersOnline.value.splice(index, 1)
    }
  }

  // ---------------------------------------------------------------------------
  // Setters (Vuex mutation compat)
  // ---------------------------------------------------------------------------

  function setChannelName(name: string): void {
    channelName.value = name
  }

  // ---------------------------------------------------------------------------
  // Return
  // ---------------------------------------------------------------------------

  return {
    // State
    channelName,
    isConnected,
    usersOnline,
    commonChannel,
    privateChannel,
    commonChannelSubscribed,
    lastReportsEventAt,
    reportsEventsCount,

    // Getters
    usersOnlineCount,
    channel,

    // Actions — lifecycle
    connect,
    disconnect,
    reconnect,

    // Actions — channels
    listenToCommonChannel,
    isCommonChannelSubscribed,
    listenToPrivateChannel,
    listenToUserChannel,
    leaveUserChannel,

    // Setters
    setChannelName,
  }
})
